import React, { useContext, useState } from "react";
import styled from "styled-components";
import { DataContext } from "../../../app/ApiContext";
import { CASHFREE_MODE } from "../../../app/constant";
import LoadingDots from "../../Atoms/LoadingDots/LoadingDots";
import { toast } from "react-toastify";
import { load } from "@cashfreepayments/cashfree-js";
import sendFirebaseEvent from "../../../analytics/FirebaseAnalytics";

const PlanModal = ({ plan, onClose, formatToINR }) => {
  const [selected, setSelected] = useState("yearly");
  const [userData, setUserData] = useState({
    name: "",
    studio_name: "",
    email: "",
    phone: "",
    coupon: "",
  });
  const { createOrder, completePayment, sendOtp, registerUser } =
    useContext(DataContext);
  const [orderId, setOrderId] = useState();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  // const [showOtp, setShowOtp] = useState(false);
  // const [otp, setOtp] = useState("");
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const openPaymentGateway = async ({
    providerData,
    accessToken,
    amount,
    planDetails,
    isYearlyPlan,
    planDiscount,
  }) => {
    const paymentProvider = providerData?.payment_provider;

    if (paymentProvider === "cashfree") {
      const paymentSessionId =
        providerData?.payment_session_id ||
        providerData?.paymentSessionId ||
        providerData?.payment_session?.payment_session_id;

      if (!paymentSessionId) {
        throw new Error("Cashfree session id is missing");
      }

      // Session id must match Cashfree env backend used (sandbox vs production).
      const environment =
        providerData?.environment || providerData?.mode || providerData?.cashfree_mode;
      const mode =
        environment === "production" || environment === "sandbox"
          ? environment
          : CASHFREE_MODE;

      const cashfree = await load({ mode });

      const checkoutResult = await cashfree.checkout({
        paymentSessionId,
        redirectTarget: "_modal",
      });

      if (checkoutResult?.error) {
        throw new Error(
          checkoutResult.error?.message || "Cashfree payment failed"
        );
      }

      const paymentStatus =
        checkoutResult?.paymentDetails?.paymentStatus ||
        checkoutResult?.paymentDetails?.payment_status;
      if (paymentStatus && paymentStatus !== "SUCCESS") {
        throw new Error("Cashfree payment not completed");
      }

      await completePayment(
        {
          settlement_for: "plan",
          payment_provider: "cashfree",
          order_id:
            providerData.order_id || providerData.cf_order_id || providerData.id,
          plan_id: planDetails.id,
          yearly_plan: Boolean(isYearlyPlan),
          plan_discount: Number(planDiscount) || 0,
          landing_page: true,
        },
        accessToken
      );
      toast.success("Payment successful");
      window.open("https://admin.kwikmemories.com/", "_blank");
      onClose();
      return;
    }

    if (paymentProvider === "razorpay" || !paymentProvider) {
      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) {
        throw new Error("Razorpay SDK failed to load");
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: providerData.amount,
        currency: "INR",
        name: "Kwik Memories",
        description: planDetails.description || planDetails.name,
        image: "https://example.com/your_logo",
        order_id: providerData.id,
        handler: async function (response) {
          setLoading(true);
          await completePayment(
            {
              plan_id: planDetails.id,
              amount: providerData.amount / 100,
              payment_id: response.razorpay_payment_id,
              signature: response.razorpay_signature,
              order_id: providerData.id,
              settlement_for: "plan",
              yearly_plan: selected === "yearly" ? "yearly_plan" : null,
              landing_page: true,
            },
            accessToken
          );
          setLoading(false);
          toast.success("Payment successful");
          window.open("https://admin.kwikmemories.com/", "_blank");
          onClose();
        },
        prefill: {
          name: `${userData?.name || ""}`,
          email: `${userData?.email || ""}`,
          contact: `${userData?.phone || ""}`,
        },
        notes: {
          address: "Kwik Media Address.",
        },
        theme: {
          color: "#AE2AB1",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        console.log("payment failed", response);
      });
      rzp.on("payment.cancelled", () => {
        alert("Payment cancelled");
      });
      rzp.open();
      return;
    }

    throw new Error("Unknown payment provider response");
  };

  const isValidEmail = (email) => {
    // Simple RFC 5322 compliant regex for most common cases
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };
  const handleVerify = async () => {
    setError(null);
    setLoading(true);
    try {
      if (!userData.name || !userData.phone) {
        setError("Please fill in name and phone number before proceeding.");
        return;
      }
      if (userData.email && !isValidEmail(userData.email)) {
        setError("Please enter a valid email address.");
        return;
      }

      if (userData.phone.length !== 10 || !/^[0-9]+$/.test(userData.phone)) {
        setError("Please enter a valid 10-digit phone number.");
        return;
      }

      const response = await sendOtp({
        phone_number: `+91${userData.phone}`,
      });
      if (response.status === 200) {
        setOrderId(response.data.orderId);
        // setShowOtp(true);
        const payload = {
          phone_number: Number(userData.phone),
          country_code: "+91",
          role: 3,
          username: userData.name,
          name: userData.name,
        };

        if (userData.email?.trim()) {
          payload.email = userData.email.trim();
        }

        if (userData.studio_name?.trim()) {
          payload.studio_name = userData.studio_name.trim();
        }

        const res = await registerUser(payload);
        console.log("Register User Response:", res);
        if (res?.status === 200) {
          console.log("✅ User registered successfully!");
          let payload = {
            storage_plan_id: plan.id,
            duration: plan.plan_pack_duration,
            settlement_for: "plan",
            plan_discount:
              userData.coupon === "FAIR50"
                ? selected === "yearly"
                  ? 50
                  : 0
                : 0,
          };
          if (selected === "yearly") {
            payload.yearly_plan = "yearly_plan";
          }
          const orderRes = await createOrder(payload, res.data.access_token);
          const providerData = orderRes?.data?.data || orderRes?.data || {};
          const amount =
            selected === "yearly"
              ? Number(plan.storage_price_per_year ?? 0)
              : plan.offer_price;

          await openPaymentGateway({
            providerData,
            accessToken: res.data.access_token,
            amount,
            planDetails: plan,
            isYearlyPlan: selected === "yearly",
            planDiscount: payload.plan_discount,
          });
        } else {
          setError(res?.message || "Failed to register user");
        }
        console.log("response.data.orderId", response.data.orderId);
        console.log("response", response);
        console.log("userData", userData);
      } else {
        setLoading(false);
        setError(response.message);
      }
    } catch (error) {
      console.log("error", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  console.log("orderId", orderId);
  // console.log('otp', otp)

  return (
    <ModalOverlay>
      <ModalContent>
        {/* <Icon>💳</Icon> */}
        <p>
          <strong>Selected Plan :</strong> {selected === "yearly" ? `12 Months` : `${plan.plan_pack_duration} Month`}
        </p>
        <p>
          <strong>Price :</strong> <del>{plan.originalPrice}</del> {plan.price}
          {formatToINR(plan.offer_price * plan.plan_pack_duration)} (
          <del>
            {formatToINR(plan.price_per_month * plan.plan_pack_duration)}
          </del>
          )
        </p>
        <p>
          <ToggleWrapper>
            <Label>
              <Radio
                name="subscription"
                value="yearly"
                checked={selected === "yearly"}
                onChange={() => setSelected("yearly")}
              />
              Yearly
            </Label>

            <Label>
              <Radio
                name="subscription"
                value="monthly"
                checked={selected === "monthly"}
                onChange={() => setSelected("monthly")}
              />
              Monthly
            </Label>
          </ToggleWrapper>
        </p>
        <p className="!text-[28px] font-black !text-[#000]">
          <span className="font-black !text-[#fff]">Total Pay :</span>{" "}
          {formatToINR(
            selected === "yearly"
              ? Number(plan.storage_price_per_year ?? 0)
              : plan.offer_price
          )}
        </p>

        {/* User input fields */}
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "10vh",
            }}
          >
            <LoadingDots />
          </div>
        ) : (
          <div className="text-left bg-white p-4 rounded-3xl gap-2 flex flex-col">
            <div>
              <div className="text-left text-[#A0A0A0] text-sm">Enter your name <span style={{ color: '#000000' }}>*</span> :</div>
              <Input
                type="text"
                placeholder="Enter your name"
                value={userData.name}
                onChange={(e) => {
                  setUserData({ ...userData, name: e.target.value });
                  setError(null);
                }}
              />
            </div>
            <div>
              <div className="text-left text-[#A0A0A0] text-sm">Enter your studio name :</div>
              <Input
                type="text"
                placeholder="Enter your studio name"
                maxLength={120}
                value={userData.studio_name}
                onChange={(e) => {
                  setUserData({ ...userData, studio_name: e.target.value });
                  setError(null);
                }}
              />
            </div>
            <div>
              <div className="text-left text-[#A0A0A0] text-sm">Enter your email :</div>
              <Input
                type="email"
                placeholder="Enter your email"
                value={userData.email}
                onChange={(e) => {
                  setUserData({ ...userData, email: e.target.value });
                  setError(null);
                }}
              />
            </div>

            <div>
              <div className="text-left text-[#A0A0A0] text-sm">Enter your phone number <span style={{ color: '#000000' }}>*</span> :</div>
              <Input
                type="tel"
                placeholder="Enter phone number"
                value={userData.phone}
                maxLength={10}
                onChange={(e) => {
                  setUserData({ ...userData, phone: e.target.value });
                  setError(null);
                }}
              />{" "}
            </div>

            <div>
              <div className="text-left text-[#A0A0A0] text-sm">Enter your coupon code :</div>
              <Input
                type="text"
                placeholder="Enter Coupon Code"
                value={userData.coupon}
                maxLength={10}
                onChange={(e) => {
                  setUserData({ ...userData, coupon: e.target.value });
                  setError(null);
                }}
              />{" "}
            </div>
          </div>
        )}
        {/* {showOtp && <Input
                    type="text"
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => {
                        setOtp(e.target.value)
                        setError(null)
                    }}
                />} */}
        {error && <p style={{ color: "red", textAlign: "left" }}>{error}</p>}
        <ButtonRow>
          <CancelButton
            onClick={() => {
              onClose();
              sendFirebaseEvent(
                "Subscription",
                "Cancel",
                "click",
                "click on cancel"
              );
            }}
          >
            Cancel
          </CancelButton>
          <BuyButton
            onClick={() => {
              handleVerify();
              sendFirebaseEvent(
                "Subscription",
                "Buy Now",
                "click",
                "click on buy now"
              );
            }}
          >
            Buy Now
          </BuyButton>
        </ButtonRow>
      </ModalContent>
    </ModalOverlay>
  );
};

export default PlanModal;

/* ---------- MODAL STYLES ---------- */

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
background: linear-gradient(144.01deg, #FFC951 0%, #C94DAA 100%);
  border-radius: 1rem;
  padding: 2rem;
  width: 450px;
  text-align: center;
  animation: fadeIn 0.3s ease;

  p {
    margin: 0.5rem 0;
    color: #ffffff;
    font-size: 1.2rem;
  }
  @media (max-width: 768px) {
    width: auto;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem;
  margin: 0.5rem 0;
  border-radius: 100px;
  font-size: 0.9rem;
background: #F7F7F7;
padding-left: 16px;

  &:focus {
    outline: none;
  }
   &::placeholder {
      color: #000;
    }
    &:focus::placeholder {
      color: transparent;
    }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 1.5rem;
`;

const CancelButton = styled.button`
  border: none;
  border-radius: 100px;
  padding: 0.8rem 3.5rem;
  cursor: pointer;
  color: #fff;
  font-size: 1.2rem;
  background: #FFFFFF4D;
  font-weight: 700;


  &:hover {
   background: #FFFFFF4D;
    transform: scale(1.05);

  }
`;

const BuyButton = styled.button`
 background: #fff;
  border: none;
  border-radius: 100px;
  padding: 0.8rem 2.5rem;
  cursor: pointer;
  color: black;
  font-size: 1.2rem;
  font-weight: 700;
  transition: 0.3s;

  &:hover {
    transform: scale(1.05);
  }
`;

const ToggleWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 16px;
  gap: 8px;

  &:selected {
    color: #000;
  }
`;

const Radio = styled.input.attrs({ type: "radio" })`
  appearance: none;
  width: 18px;
  height: 18px;
  border: 2px solid #000;
  border-radius: 50%;
  outline: none;
  cursor: pointer;
  position: relative;
  background-color: transparent;

  &:checked {
    background-color: #000;
    box-shadow: inset 0 0 0 4px #f5ae65;
  }
`;
